import React, { useRef, useState } from 'react'
import { Button, CircularProgress, ListItemText, Menu, MenuItem } from '@mui/material'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { getOperationDetail } from './operationLibraryApi'

/**
 * OperationVersionControl — story 167 (D10 · RN-07.3, CA5.9, CA7.3). The small "v3 ▾" of an operation row. It
 * opens, next to the row, the versions of that operation (N2, asked every time it opens) with what "Select
 * version" showed (#130 K31): number, state, who and when — drafts and proposals included (#152 RN-01.6).
 * Picking one makes it the one that is dragged and closes the list. A failure says so and changes nothing
 * (E-15). No dialog anywhere.
 */
const VERSIONS_LOADING = { loading: true, failed: false, list: [] }

const OperationVersionControl = ({ code, versionNumber, chosenNodeId, onVersionChosen }) => {
  const { t_ } = useSmartTranslation()
  const buttonRef = useRef()
  const [open, setOpen] = useState(false)
  const [versions, setVersions] = useState(VERSIONS_LOADING)

  const openVersions = async (event) => {
    event.stopPropagation()
    setOpen(true)
    setVersions(VERSIONS_LOADING)
    try {
      const detail = await getOperationDetail(code)
      const list = [...(detail?.versions || [])].sort((one, other) => (other.versionNumber || 0) - (one.versionNumber || 0))
      setVersions({ loading: false, failed: false, list })
    } catch (thrown) {
      setVersions({ loading: false, failed: true, list: [] })
    }
  }

  return (
    <>
      <Button
        ref={buttonRef}
        size='small'
        variant='outlined'
        data-testid={'operation-version-control-' + code}
        aria-label={t_('Select version')}
        onClick={openVersions}
        sx={{ flex: '0 0 auto', minWidth: 0, px: 0.75, py: 0, fontSize: 11.5, lineHeight: 1.7, textTransform: 'none' }}
      >
        {'v' + versionNumber + ' ▾'}
      </Button>
      <Menu
        anchorEl={buttonRef.current}
        open={open}
        onClose={() => setOpen(false)}
        onClick={(event) => event.stopPropagation()}
        MenuListProps={{ dense: true, 'data-testid': 'operation-palette-versions' }}
      >
        {versions.loading ? <MenuItem disabled><CircularProgress size={14} /></MenuItem> : null}
        {versions.failed
          ? <MenuItem disabled data-testid='operation-palette-versions-failed'>{t_('The versions could not be loaded')}</MenuItem>
          : null}
        {versions.list.map((version) => (
          <MenuItem
            key={version.nodeId}
            data-testid={'operation-palette-version-' + version.versionNumber}
            selected={version.nodeId === chosenNodeId}
            onClick={() => {
              setOpen(false)
              onVersionChosen(version)
            }}
          >
            <ListItemText
              primary={t_('Version') + ' ' + version.versionNumber + (version.indexValue ? ' · ' + version.indexValue : '')}
              secondary={[version.createdBy, version.createdAt].filter(Boolean).join(' · ')}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

export default OperationVersionControl
